import useSessionStore from "@features/session/hooks/useSessionStore";
import { isWeb } from "@helpers/platform";
import { useDevice } from "@hooks/useDevice";
import type {
	BaseItemSchema,
	ItemResponse,
	OriginalItemTypes,
} from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { ConversationRenderer } from "@memorang/ui/components/ConversationRenderer";
import { Caption } from "react-native-paper";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import ItemWrapper from "../../ItemWrapper";
import Writing from "./variants/Writing";

type Props = {
	item: BaseItemSchema;
	response: Extract<ItemResponse, { type: "writing" }>;
	toggleShowReadMore?: () => void;
	originalItemType: OriginalItemTypes;
};

const WritingContainer = ({
	item,
	response,
	toggleShowReadMore,
	originalItemType,
}: Props) => {
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const currentUserResponse =
		item.answer?.aiResponse?.answer || userResponse?.[item.id!];
	const { isMobile } = useDevice();
	const itemStates = useSessionStore((state) => state.itemStates);
	const itemState = itemStates?.get(item.id!);
	const setUserResponse = useAIFeedbackStore((state) => state.setUserResponse);
	const handleChangeText = (value: string) => {
		setUserResponse(item.id!, value);
	};
	const discussion = item.content?.discussion;

	const messages = discussion?.map((item) => ({
		name: item.student.name,
		avatarUrl: item.student.avatar.url,
		content: item.post,
	}));
	const showConversationView = messages && messages?.length > 0;

	const instruction = item.content?.instruction;
	const hasStem = item.content?.stem;
	const isAnswered = itemState?.answered || item.answer != null;

	return (
		<ItemWrapper
			item={item}
			toggleShowReadMore={toggleShowReadMore}
			response={response}
			originalItemType={originalItemType}
			instructions={
				hasStem
					? typeof instruction === "string"
						? instruction
						: instruction?.text
					: undefined
			}
			footer={
				!isWeb && (
					<Writing
						value={currentUserResponse as string}
						onChangeText={handleChangeText}
						key={item.id}
						disabled={isAnswered}
					/>
				)
			}
		>
			<Box
				paddingHorizontal={isWeb && !isMobile ? 0 : 16}
				gap={16}
				paddingBottom={isWeb ? 0 : 100}
			>
				{showConversationView ? (
					<ConversationRenderer messages={messages} />
				) : null}
				{instruction && !hasStem && (
					<Caption>
						{typeof instruction === "string" ? instruction : instruction.text}
					</Caption>
				)}
				{isWeb && (
					<Writing
						value={currentUserResponse as string}
						onChangeText={handleChangeText}
						key={item.id}
						disabled={isAnswered}
					/>
				)}
			</Box>
		</ItemWrapper>
	);
};

export default WritingContainer;
